import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { isCmsSuperAdmin } from '@shared/constants/cmsAdmin';
import { supabase } from '../lib/supabase';
import { buildPublicApiUrl } from '@shared/lib/publicApiUrl';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AdminAuthState {
  status: AuthStatus;
  user: User | null;
  session: Session | null;
  accessDenied: boolean;
  signOut: () => Promise<void>;
  clearAccessDenied: () => void;
}

const AdminAuthContext = createContext<AdminAuthState | null>(null);

interface AdminAuthProviderProps {
  children: React.ReactNode;
}

const API_BASE = import.meta.env.VITE_API_BASE || '';

async function rejectUnauthorizedSession(accessToken: string): Promise<void> {
  const url = buildPublicApiUrl(API_BASE, '/api/cms/admin/reject-session');
  await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  }).catch((err: unknown) => {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.warn('[warn] [site:adminAuth] Failed to reject unauthorized session', { message });
  });
}

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const rejectingRef = useRef(false);

  const enforceSuperAdmin = useCallback(async (nextSession: Session | null) => {
    if (!nextSession?.user) {
      setSession(null);
      setUser(null);
      setStatus('unauthenticated');
      return;
    }

    if (isCmsSuperAdmin(nextSession.user.id)) {
      setSession(nextSession);
      setUser(nextSession.user);
      setStatus('authenticated');
      setAccessDenied(false);
      return;
    }

    if (rejectingRef.current) {
      return;
    }

    rejectingRef.current = true;
    setAccessDenied(true);

    try {
      await rejectUnauthorizedSession(nextSession.access_token);
      await supabase.auth.signOut();
    } finally {
      rejectingRef.current = false;
      setSession(null);
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    let initialSessionResolved = false;

    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      initialSessionResolved = true;
      void enforceSuperAdmin(existing);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!initialSessionResolved) {
          return;
        }

        void enforceSuperAdmin(newSession);
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [enforceSuperAdmin]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const clearAccessDenied = useCallback(() => {
    setAccessDenied(false);
  }, []);

  const value = useMemo<AdminAuthState>(
    () => ({ status, user, session, accessDenied, signOut, clearAccessDenied }),
    [status, user, session, accessDenied, signOut, clearAccessDenied],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthState {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
